import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, PlusCircle, LogOut, Brain, BookOpen, Lightbulb,
  Plus, Trash2, Save, Edit3, X, Check, Loader2, Search, Filter, Tag,
  ChevronDown, ChevronUp, AlertCircle, ClipboardList, Settings, Copy, Sparkles
} from 'lucide-react';
import Logo from '../../components/common/Logo';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import apiService from '../../services/api';

const CATEGORIES = [
  { id: 'article', label: 'Artigo', icon: BookOpen, color: '#8F7AC5' },
  { id: 'method', label: 'Método', icon: Lightbulb, color: '#F5A623' },
  { id: 'pillar', label: 'Pilar do Sono', icon: Brain, color: '#D98DAE' },
  { id: 'tip', label: 'Dica Prática', icon: Tag, color: '#7BC47F' },
];

const AGE_RANGE_OPTIONS = [
  { id: '0-3', label: '0-3 meses' },
  { id: '4-6', label: '4-6 meses' },
  { id: '7-9', label: '7-9 meses' },
  { id: '10-12', label: '10-12 meses' },
  { id: '13-24', label: '1-2 anos' },
];

const AdminTrainingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal de edição
  const [showModal, setShowModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: 'article',
    content: '',
    age_ranges: [],
    tags: []
  });
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    loadData();
  }, [selectedCategory]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [materialsData, statsData] = await Promise.all([
        apiService.getTrainingMaterials(selectedCategory),
        apiService.getTrainingStats()
      ]);
      setMaterials(materialsData);
      setStats(statsData);
    } catch (error) {
      console.error('Erro ao carregar materiais:', error);
    } finally {
      setLoading(false);
    }
  };

  const openNewMaterial = () => {
    setEditingMaterial(null);
    setFormData({
      title: '',
      category: 'article',
      content: '',
      age_ranges: [],
      tags: []
    });
    setShowModal(true);
  };

  const openEditMaterial = (material) => {
    setEditingMaterial(material);
    setFormData({
      title: material.title,
      category: material.category,
      content: material.content,
      age_ranges: material.age_ranges || [],
      tags: material.tags || []
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      alert('Título e conteúdo são obrigatórios');
      return;
    }
    
    setSaving(true);
    try {
      if (editingMaterial) {
        await apiService.updateTrainingMaterial(editingMaterial.id, formData);
      } else {
        await apiService.createTrainingMaterial(formData);
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar material');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (materialId) => {
    if (!window.confirm('Tem certeza que deseja excluir este material?')) return;
    
    try {
      await apiService.deleteTrainingMaterial(materialId);
      loadData();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      alert('Erro ao excluir material');
    }
  };

  const toggleAgeRange = (rangeId) => {
    setFormData(prev => ({
      ...prev,
      age_ranges: prev.age_ranges.includes(rangeId)
        ? prev.age_ranges.filter(r => r !== rangeId)
        : [...prev.age_ranges, rangeId]
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const filteredMaterials = materials.filter(m => 
    m.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryInfo = (categoryId) => {
    return CATEGORIES.find(c => c.id === categoryId) || CATEGORIES[0];
  };

  return (
    <div className="min-h-screen bg-[#FBF4FF]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-[#FAE0D5] p-6 hidden lg:block">
        <div className="mb-8">
          <Link to="/admin/dashboard">
            <Logo variant="v3SonoRotina" size="md" className="cursor-pointer hover:opacity-80 transition-opacity" />
          </Link>
        </div>

        <nav className="space-y-2">
          {[
            { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
            { icon: PlusCircle, label: 'Novo Relatório', path: '/admin/new' },
            { icon: ClipboardList, label: 'Inserir Manualmente', path: '/admin/manual' },
            { icon: FileText, label: 'Relatórios', path: '/admin/reports' },
            { icon: Copy, label: 'Templates', path: '/admin/templates' },
            { icon: Sparkles, label: 'Treinar IA', path: '/admin/training' },
            { icon: Settings, label: 'Editor de Formulário', path: '/admin/form-builder' }
          ].map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                location.pathname === item.path
                  ? 'bg-[#8F7AC5] text-white'
                  : 'text-[#6B5B8C] hover:bg-[#FBF4FF]'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            variant="ghost"
            className="w-full justify-start text-[#9890A8] hover:text-[#30244F] hover:bg-[#FBF4FF]"
            onClick={() => navigate('/admin')}
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        {/* Mobile Header */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <Link to="/admin/dashboard">
              <Logo variant="v3SonoRotina" size="sm" />
            </Link>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/admin')}
              className="text-[#9890A8]"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
          {/* Mobile tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {[
              { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
              { icon: FileText, label: 'Relatórios', path: '/admin/reports' },
              { icon: Copy, label: 'Templates', path: '/admin/templates' },
              { icon: Sparkles, label: 'Treinar IA', path: '/admin/training' },
              { icon: Settings, label: 'Formulário', path: '/admin/form-builder' }
            ].map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  location.pathname === item.path
                    ? 'bg-[#8F7AC5] text-white'
                    : 'bg-white text-[#6B5B8C] border border-[#FAE0D5]'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-[#30244F] flex items-center gap-3">
                <Brain className="w-8 h-8 text-[#8F7AC5]" />
                Treinar IA
              </h1>
              <p className="text-[#6B5B8C] mt-1">
                Adicione artigos, métodos e dicas para a IA usar ao gerar planos de sono
              </p>
            </div>
            <Button
              onClick={openNewMaterial}
              className="bg-[#8F7AC5] hover:bg-[#7A68A8]"
            >
              <Plus className="w-5 h-5 mr-2" />
              Novo Material
            </Button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <p className="text-sm text-[#9890A8]">Total</p>
                <p className="text-2xl font-bold text-[#30244F]">{stats.total}</p>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <p className="text-sm text-[#9890A8]">Ativos</p>
                <p className="text-2xl font-bold text-[#7BC47F]">{stats.active}</p>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <p className="text-sm text-[#9890A8]">Artigos</p>
                <p className="text-2xl font-bold text-[#8F7AC5]">{stats.by_category?.article || 0}</p>
              </div>
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <p className="text-sm text-[#9890A8]">Métodos</p>
                <p className="text-2xl font-bold text-[#F5A623]">{stats.by_category?.method || 0}</p>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9890A8]" />
              <Input
                placeholder="Buscar materiais..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-[#FAE0D5]"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto">
              <Button
                variant={selectedCategory === null ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(null)}
                className={selectedCategory === null ? 'bg-[#30244F]' : 'border-[#FAE0D5]'}
              >
                Todos
              </Button>
              {CATEGORIES.map(cat => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={selectedCategory === cat.id ? '' : 'border-[#FAE0D5]'}
                  style={selectedCategory === cat.id ? { backgroundColor: cat.color } : {}}
                >
                  <cat.icon className="w-4 h-4 mr-1" />
                  {cat.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Materials List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#8F7AC5] animate-spin" />
            </div>
          ) : filteredMaterials.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl shadow-sm">
              <Brain className="w-16 h-16 text-[#FAE0D5] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#30244F] mb-2">Nenhum material encontrado</h3>
              <p className="text-[#9890A8] mb-4">
                {searchTerm ? 'Tente outra busca' : 'Comece adicionando artigos e métodos para treinar a IA'}
              </p>
              <Button onClick={openNewMaterial} className="bg-[#8F7AC5]">
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Material
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredMaterials.map((material) => {
                const catInfo = getCategoryInfo(material.category);
                const CatIcon = catInfo.icon;
                
                return (
                  <motion.div
                    key={material.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${catInfo.color}20` }}
                      >
                        <CatIcon className="w-6 h-6" style={{ color: catInfo.color }} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-semibold text-[#30244F] mb-1">{material.title}</h3>
                            <div className="flex items-center gap-2 text-sm text-[#9890A8] mb-2">
                              <span 
                                className="px-2 py-0.5 rounded-full text-xs font-medium"
                                style={{ backgroundColor: `${catInfo.color}20`, color: catInfo.color }}
                              >
                                {catInfo.label}
                              </span>
                              {material.age_ranges?.length > 0 && (
                                <span className="text-xs">
                                  {material.age_ranges.join(', ')} meses
                                </span>
                              )}
                              {material.age_ranges?.length === 0 && (
                                <span className="text-xs text-[#7BC47F]">Todas as idades</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditMaterial(material)}
                              className="text-[#8F7AC5]"
                            >
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(material.id)}
                              className="text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        <p className="text-[#6B5B8C] text-sm line-clamp-3">
                          {material.content}
                        </p>
                        
                        {material.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {material.tags.map(tag => (
                              <span 
                                key={tag}
                                className="px-2 py-0.5 bg-[#FBF4FF] text-[#8F7AC5] text-xs rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Modal de Edição */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#30244F]">
                  {editingMaterial ? 'Editar Material' : 'Novo Material'}
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-6">
                {/* Título */}
                <div>
                  <Label className="text-[#30244F]">Título *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Método dos 6 Pilares do Sono"
                    className="border-[#FAE0D5] mt-1"
                  />
                </div>

                {/* Categoria */}
                <div>
                  <Label className="text-[#30244F]">Categoria *</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {CATEGORIES.map(cat => {
                      const CatIcon = cat.icon;
                      const isSelected = formData.category === cat.id;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => setFormData({ ...formData, category: cat.id })}
                          className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 transition-all ${
                            isSelected 
                              ? 'border-transparent text-white' 
                              : 'border-[#FAE0D5] text-[#6B5B8C] hover:border-[#8F7AC5]'
                          }`}
                          style={isSelected ? { backgroundColor: cat.color } : {}}
                        >
                          <CatIcon className="w-4 h-4" />
                          {cat.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Conteúdo */}
                <div>
                  <Label className="text-[#30244F]">Conteúdo *</Label>
                  <p className="text-xs text-[#9890A8] mb-1">
                    Escreva o conteúdo que a IA deve usar como referência ao gerar planos
                  </p>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Descreva o método, artigo ou dica em detalhes..."
                    className="border-[#FAE0D5] min-h-[200px]"
                  />
                </div>

                {/* Faixas Etárias */}
                <div>
                  <Label className="text-[#30244F]">Faixas Etárias</Label>
                  <p className="text-xs text-[#9890A8] mb-2">
                    Deixe vazio para aplicar a todas as idades
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {AGE_RANGE_OPTIONS.map(range => (
                      <button
                        key={range.id}
                        onClick={() => toggleAgeRange(range.id)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          formData.age_ranges.includes(range.id)
                            ? 'bg-[#8F7AC5] text-white'
                            : 'bg-[#FBF4FF] text-[#6B5B8C] border border-[#FAE0D5]'
                        }`}
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <Label className="text-[#30244F]">Tags</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Adicionar tag..."
                      className="border-[#FAE0D5]"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    />
                    <Button onClick={addTag} variant="outline" className="border-[#8F7AC5] text-[#8F7AC5]">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.tags.map(tag => (
                        <span 
                          key={tag}
                          className="px-3 py-1 bg-[#FBF4FF] text-[#8F7AC5] text-sm rounded-full flex items-center gap-1"
                        >
                          {tag}
                          <button onClick={() => removeTag(tag)} className="hover:text-red-500">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Botões */}
                <div className="flex gap-4 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowModal(false)}
                    className="flex-1 border-[#FAE0D5]"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 bg-[#8F7AC5] hover:bg-[#7A68A8]"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Salvar
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminTrainingPage;
